import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    CardContent,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Alert,
    Tooltip
} from "@mui/material";
import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { format, isValid, parse } from "date-fns";
import MainCard from "../../../components/cards/MainCard";
import LoadingSwitch from "../../../components/LoadingSwitch";
import { useTheme } from "@mui/material/styles";
import { BATCH_SEND_ERROR, isBatchSendValid } from "../batchSendValidation";

DeliverySection.propTypes = {
    delivery: PropTypes.string.isRequired,
    handleDeliveryChange: PropTypes.func.isRequired,
    scheduledDate: PropTypes.instanceOf(Date),
    setScheduledDate: PropTypes.func.isRequired,
    startDate: PropTypes.instanceOf(Date),
    setStartDate: PropTypes.func.isRequired,
    startTime: PropTypes.string,
    setStartTime: PropTypes.func.isRequired,
    endDate: PropTypes.instanceOf(Date),
    setEndDate: PropTypes.func.isRequired,
    endTime: PropTypes.string,
    setEndTime: PropTypes.func.isRequired,
    pauseOnWeekends: PropTypes.bool.isRequired,
    setPauseOnWeekends: PropTypes.func.isRequired,
    isMultipleUsersSelected: PropTypes.bool,
    recipientType: PropTypes.string
};

export default function DeliverySection({
    delivery,
    handleDeliveryChange,
    scheduledDate,
    setScheduledDate,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    pauseOnWeekends,
    setPauseOnWeekends,
    isMultipleUsersSelected,
    recipientType
}) {
    const [isWeekendPauseLoading, setIsWeekendPauseLoading] = useState(false);
    const [showBatchSendWarning, setShowBatchSendWarning] = useState(false);

    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";

    // Custom handler for delivery change
    const handleDeliveryChangeWithWarning = (event, newDelivery) => {
        if (newDelivery === "batchSend" && isMultipleUsersSelected) {
            setShowBatchSendWarning(true);
            return; // Don't update the delivery option
        }

        // Reset warning if switching away from batch send
        if (newDelivery !== "batchSend") {
            setShowBatchSendWarning(false);
        }

        if (newDelivery !== null) {
            handleDeliveryChange(event, newDelivery);
        }
    };

    const handleWeekendPauseChange = async () => {
        setIsWeekendPauseLoading(true);
        try {
            // Simulate an async operation - replace with actual API call if needed
            await new Promise(resolve => setTimeout(resolve, 1000));
            setPauseOnWeekends(!pauseOnWeekends);
        } finally {
            setIsWeekendPauseLoading(false);
        }
    };


    const isScheduledValid = scheduledDate && scheduledDate > new Date();
    const batchSendValid = isBatchSendValid(startDate, startTime, endDate, endTime);

    // Half-typed values are ignored so state only ever holds a complete date or time.
    const setDateIfValid = (setter) => (date) => {
        if (isValid(date)) setter(date);
    };
    const setTimeIfValid = (setter) => (time) => {
        if (isValid(time)) setter(format(time, "HH:mm"));
    };
    const toTime = (time) => (time ? parse(time, "HH:mm", new Date()) : null);
    const fieldSlotProps = (error) => ({ textField: { fullWidth: true, sx: { mb: 2 }, error } });

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                <ToggleButtonGroup
                    exclusive
                    value={delivery}
                    onChange={handleDeliveryChangeWithWarning}
                    aria-label="delivery"
                    sx={{
                        '& .MuiToggleButton-root': {
                            whiteSpace: 'nowrap'
                        }
                    }}
                >
                    <ToggleButton value="sendNow" aria-label="Send Now">
                        Send Immediately
                    </ToggleButton>
                    <ToggleButton value="schedule" aria-label="Schedule">
                        Send later
                    </ToggleButton>
                    <Tooltip
                        title={isMultipleUsersSelected ? "Batch sending is not available when selecting multiple users. Please select all users or a different recipient type for batch sending." : ""}
                        arrow
                        placement="top"
                        disableHoverListener={!isMultipleUsersSelected}
                    >
                        <span> {/* Wrapper needed because disabled elements can't receive events */}
                            <ToggleButton
                                value="batchSend"
                                aria-label="Batch Send"
                                disabled={isMultipleUsersSelected}
                            >
                                Send in batches
                            </ToggleButton>
                        </span>
                    </Tooltip>
                </ToggleButtonGroup>
            </Box>

            {delivery === "sendNow" && (
                <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: 'center', mt: 12 }}>
                    <MainCard sx={{
                        width: "50%",
                        textAlign: "center",
                        mt: 5,
                        borderRadius: 2,
                        bgcolor: lightMode ? theme.palette.white : theme.palette.grey[700],
                    }}>
                        <CardContent>
                            <Typography variant="h5" component="div">
                                Courses/Simulations will be sent immediately.
                            </Typography>
                        </CardContent>
                    </MainCard>
                </Box>
            )}

            {delivery === "schedule" && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ width: "50%", mb: 2 }}>
                        <DateTimePicker
                            label="Scheduled Date and Time"
                            value={scheduledDate ?? null}
                            onChange={setDateIfValid(setScheduledDate)}
                            format="yyyy-MM-dd HH:mm"
                            ampm={false}
                            slotProps={fieldSlotProps(!isScheduledValid)}
                        />
                    </Box>
                    {!isScheduledValid && (
                        <Typography variant="caption" sx={{ color: "error.main", mt: 1 }}>
                            Scheduled date and time must be in the future.
                        </Typography>
                    )}
                </Box>
            )}

            {delivery === "batchSend" && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ width: "50%", mb: 2 }}>
                        <DatePicker
                            label="Start Date"
                            value={startDate ?? null}
                            onChange={setDateIfValid(setStartDate)}
                            format="yyyy-MM-dd"
                            slotProps={fieldSlotProps(!batchSendValid)}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate ?? null}
                            onChange={setDateIfValid(setEndDate)}
                            format="yyyy-MM-dd"
                            slotProps={fieldSlotProps(!batchSendValid)}
                        />
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Between the hours
                        </Typography>
                        <TimePicker
                            label="Start Time"
                            value={toTime(startTime)}
                            onChange={setTimeIfValid(setStartTime)}
                            ampm={false}
                            format="HH:mm"
                            slotProps={fieldSlotProps(!batchSendValid)}
                        />
                        <TimePicker
                            label="End Time"
                            value={toTime(endTime)}
                            onChange={setTimeIfValid(setEndTime)}
                            ampm={false}
                            format="HH:mm"
                            slotProps={fieldSlotProps(!batchSendValid)}
                        />
                    </Box>
                    {!batchSendValid && (
                        <Typography variant="caption" sx={{ color: "error.main", mt: 1, mb: 2 }}>
                            {BATCH_SEND_ERROR}
                        </Typography>
                    )}
                    <FormControlLabel
                        control={
                            <LoadingSwitch
                                checked={pauseOnWeekends}
                                onChange={handleWeekendPauseChange}
                                loading={isWeekendPauseLoading}
                            />
                        }
                        label="Pause on Weekends"
                    />
                </Box>
            )}
        </Box>
    );
}